import { useState, useCallback } from 'react';
import { ApiService } from '../services/ApiService';
import { serializeNode } from '../utils/serializeNode';
import type { StrategyNode } from '../types/BaseNode';

function extractPreviewRootCount(response: unknown): number | null {
    if (!response || typeof response !== 'object') {
        return null;
    }

    const record = response as Record<string, unknown>;
    if (typeof record.row_count === 'number' && Number.isFinite(record.row_count)) {
        return record.row_count;
    }
    if (Array.isArray(record.data)) {
        return record.data.length;
    }
    return null;
}

/**
 * Shared hook for fetching and managing node-level preview data.
 * Centralizes state and API communication for builder components.
 */
export function useNodePreview<T = any>(
    node: StrategyNode,
    globalStart: string | null,
    globalEnd: string | null,
    onMetadataUpdate?: (metadata?: Record<string, unknown>) => void,
    onPreviewResultUpdate?: (response: unknown, nodeId: string) => void,
) {
    const [isCalculating, setIsCalculating] = useState(false);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);
    const [result, setResult] = useState<T | null>(null);

    const calculatePreview = useCallback(async () => {
        setIsCalculating(true);
        setErrorMsg(null);

        try {
            const serialized = serializeNode(node, globalStart, globalEnd);
            const response = await ApiService.evaluateStrategy(
                serialized,
                { start_date: globalStart, end_date: globalEnd },
                node
            );

            if (response.success === false) {
                setResult(null);
                setErrorMsg(response.error || response.explanation || 'Calculation failed');
            } else {
                const previewMetadata = (response.node_metadata && typeof response.node_metadata === 'object')
                    ? { ...(response.node_metadata as Record<string, unknown>) }
                    : {};
                const rootCount = extractPreviewRootCount(response);
                if (rootCount !== null && previewMetadata[node.id] === undefined) {
                    previewMetadata[node.id] = { count: rootCount };
                }
                if (Object.keys(previewMetadata).length > 0) {
                    onMetadataUpdate?.(previewMetadata);
                }
                onPreviewResultUpdate?.(response, node.id);

                const data = response.data ?? response;
                if (Array.isArray(data) && data.length === 0) {
                    setResult([] as any);
                    setErrorMsg('No data points in this period.');
                } else {
                    setResult(data);
                }
            }
        } catch (err: any) {
            console.error('Preview calculation failed:', err);
            setErrorMsg(err.message || 'Network error');
            setResult(null);
        } finally {
            setIsCalculating(false);
        }
    }, [node, globalStart, globalEnd, onMetadataUpdate, onPreviewResultUpdate]);

    return {
        isCalculating,
        errorMsg,
        result,
        setErrorMsg,
        setResult,
        calculatePreview
    };
}
